import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, CheckCircle, Building2, ThermometerSun, Lightbulb, Shield, TrendingDown, Banknote, Factory, Layers, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const CaseStudyLondonMEES = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "MEES Case Study: London Commercial Unit Split — G242 to Band B Across 3 Units",
    "author": { "@type": "Organization", "name": "Energy Performance Direct" },
    "publisher": { "@type": "Organization", "name": "Energy Performance Direct", "url": "https://energyperformancedirect.co.uk" },
    "description": "How we helped a London landlord split a poorly insulated G-rated commercial building into 3 compliant units, creating a staged improvement roadmap from Band G to Band B with a pathway to Band A via solar PV."
  };

  const units = [
    {
      name: 'Unit 1 — Front Ground Floor',
      use: 'Offices',
      area: '126 m²',
      baseline: { band: 'F', score: 135, emissions: 21.26, energy: 220.63 },
      stages: [
        { band: 'E', score: 125, label: 'Stage 1 — Minimum MEES', measures: ['LED lighting throughout (100 lm/W)'], note: 'Single measure achieves legal minimum for letting.' },
        { band: 'D', score: 98, label: 'Stage 2 — Fabric Upgrade', measures: ['LED lighting (100 lm/W)', 'New windows (Uw 1.40 W/m²K)'], note: '27-point headroom above Band E threshold.' },
        { band: 'B', score: 44, label: 'Stage 3 — Decarbonise Heating', measures: ['LED lighting + new windows', 'AC split system ASHP (COP 3.5 / EER 2.0)', 'Heating in office, stairwell and corridor'], note: 'Meets proposed 2030 Band B MEES target.' },
        { band: 'B', score: 38, label: 'Stage 4 — Future-Proof', measures: ['All Stage 3 measures', 'Brise Soleil external shading'], note: 'Just 13 points from Band A. Full summer comfort and future-proofing.' }
      ]
    },
    {
      name: 'Unit 2 — Rear Ground Floor',
      use: 'Workshop / Storage',
      area: '78.8 m²',
      baseline: { band: 'G', score: 242, emissions: 39.60, energy: 247.46 },
      stages: [
        { band: 'E', score: 114, label: 'Stage 1 — Remove Gas Blower', measures: ['Remove gas warm air blower', 'Workshop becomes unheated', 'Retain electric heater in office'], note: 'Removing the inefficient gas blower alone achieves MEES compliance.' },
        { band: 'D', score: 96, label: 'Stage 2 — Insulate Above', measures: ['New ceiling at 2.4m height', '300mm mineral wool insulation above'], note: '29-point headroom above Band E threshold.' },
        { band: 'B', score: 32, label: 'Stage 3 — Full Upgrade', measures: ['Ceiling + insulation', 'AC split system ASHP in office (COP 3.5)', 'LED lighting throughout', 'Point-of-use instantaneous HWS'], note: 'Just 7 points from Band A. 64% running cost reduction.' }
      ]
    },
    {
      name: 'Unit 3 — First Floor',
      use: 'Offices',
      area: '210 m²',
      baseline: { band: 'G', score: 222, emissions: 76.65, energy: 448.15 },
      stages: [
        { band: 'D', score: 96, label: 'Stage 1 — Roof + Windows', measures: ['200mm mineral wool roof insulation (0.25 W/m²K)', 'New windows (Uw 1.40 W/m²K)', 'Retain gas boiler and original lighting'], note: 'Fabric-first approach skips Band E entirely to Band D. 63% heating demand reduction.' },
        { band: 'B', score: 45, label: 'Stage 2 — Decarbonise', measures: ['Roof insulation + new windows', 'AC split system ASHP in all zones', 'Point-of-use electric HWS', 'Gas boiler decommissioned'], note: 'Meets proposed 2030 Band B MEES target.' },
        { band: 'B', score: 33, label: 'Stage 3 — Complete', measures: ['All Stage 2 measures', 'LED lighting throughout (100 lm/W, 100% LOR)'], note: '72% energy reduction vs baseline. Full future-proofing.' }
      ]
    }
  ];

  const bandColors = {
    G: 'border-red-700 bg-red-50', F: 'border-red-500 bg-red-50',
    E: 'border-amber-500 bg-amber-50', D: 'border-yellow-500 bg-yellow-50',
    C: 'border-green-500 bg-green-50', B: 'border-blue-500 bg-blue-50',
    A: 'border-emerald-500 bg-emerald-50'
  };
  const bandBgColors = {
    G: 'bg-red-700', F: 'bg-red-500', E: 'bg-amber-500', D: 'bg-yellow-500',
    C: 'bg-green-500', B: 'bg-blue-500', A: 'bg-emerald-500'
  };
  const bandTextColors = {
    G: 'text-red-700', F: 'text-red-600', E: 'text-amber-700', D: 'text-yellow-700',
    C: 'text-green-700', B: 'text-blue-700', A: 'text-emerald-700'
  };

  return (
    <>
      <Helmet>
        <title>MEES Case Study | London Unit Split G to B | Commercial EPC Advice | Energy Performance Direct</title>
        <meta name="description" content="Real case study: How a poorly insulated 431 m² London commercial building at E2 9DG was split into 3 units with staged EPC improvements from G242 to Band B. Solid brick walls, flat concrete roof, mixed gas/electric heating." />
        <meta name="keywords" content="MEES case study London, commercial EPC unit split, G to B transformation, EPC advice report London, SBEM modelling commercial property, MEES compliance landlord" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/case-study-london-mees-unit-split" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Navbar />
      <Breadcrumbs items={[
        { label: 'Industrial EPC', path: '/industrial-epc' },
        { label: 'Case Study: London MEES Unit Split', path: '/case-study-london-mees-unit-split' }
      ]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16" data-testid="case-study-london-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm mb-6">
              <Building2 className="w-4 h-4" />
              Real Case Study
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              London Unit Split: G242 to Band B Across 3 Units
            </h1>
            <p className="text-lg text-slate-300 mb-4">
              How we helped a London landlord split a poorly insulated commercial building into three separately lettable units, each with a staged MEES improvement roadmap targeting Band B and a pathway to Band A.
            </p>
            <div className="flex flex-wrap gap-3 text-sm text-slate-300 mt-6">
              <span className="bg-slate-700 px-3 py-1 rounded-full">431 m² total</span>
              <span className="bg-slate-700 px-3 py-1 rounded-full">Vyner Street, London E2</span>
              <span className="bg-slate-700 px-3 py-1 rounded-full">3 Units Created</span>
              <span className="bg-slate-700 px-3 py-1 rounded-full">G to B Transformation</span>
              <span className="bg-slate-700 px-3 py-1 rounded-full">Solar PV to Band A</span>
            </div>

            {/* Rating Journey */}
            <div className="flex items-center gap-2 mt-10 flex-wrap">
              <div className="bg-red-700 text-white px-4 py-2 rounded-lg font-bold text-xl">G242</div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
              <div className="bg-amber-500 text-white px-4 py-2 rounded-lg font-bold text-xl">E</div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
              <div className="bg-yellow-500 text-white px-4 py-2 rounded-lg font-bold text-xl">D</div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
              <div className="bg-blue-500 text-white px-4 py-2 rounded-lg font-bold text-xl">B32</div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
              <div className="bg-emerald-500 text-white px-4 py-2 rounded-lg font-bold text-xl flex items-center gap-1"><Sun className="w-4 h-4" />A</div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Energy Performance Direct was commissioned to carry out a comprehensive commercial energy assessment and MEES advice report for a mixed-use building on Vyner Street in London's Bethnal Green. The client intended to split the existing single building into three separately lettable units and needed to understand the EPC implications before proceeding.
              </p>
              <p>
                The building presented some of the worst-case fabric conditions we encounter: solid brick walls with no insulation, a flat concrete roof with minimal or no insulation, single-glazed windows in poor condition, and a combination of gas warm air blowers, electric fan heaters, and a gas boiler. The whole-building baseline EPC scored G214 — well below the <Link to="/mees-commercial-buildings" className="text-green-600 hover:underline">MEES minimum of Band E</Link>. None of the three proposed units could legally be let without significant improvement works.
              </p>
              <p>
                We modelled each of the three units separately using <Link to="/sbem-explained" className="text-green-600 hover:underline">SBEM calculations</Link>, providing staged improvement pathways for each. The client could then decide which level of investment to make for each unit based on their plans — whether that was minimum compliance, future-proofing for the proposed 2030 Band B requirement, or pushing to Band A with whole-building solar PV.
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              <Card className="border-none bg-red-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-700">G214</p>
                  <p className="text-sm text-gray-600 mt-1">Whole Building Baseline</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-blue-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">B32</p>
                  <p className="text-sm text-gray-600 mt-1">Best Unit Rating</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-gray-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900">3</p>
                  <p className="text-sm text-gray-600 mt-1">Units Created</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-emerald-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">72%</p>
                  <p className="text-sm text-gray-600 mt-1">Max Energy Reduction</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Baseline Challenges */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Baseline Analysis</h2>
            <p className="text-gray-700 mb-8">
              The building presented multiple challenges typical of older London commercial properties that have never been thermally upgraded:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Fabric Deficiencies</h3>
                {[
                  { icon: Shield, title: 'Solid brick walls (1.70 W/m²K)', desc: 'Completely uninsulated solid masonry. Over 6x worse than current Part L requirement of 0.26 W/m²K.' },
                  { icon: Layers, title: 'Flat concrete roof (up to 7.14 W/m²K)', desc: 'Workshop area had effectively no roof insulation at all. First floor roof at 2.80 W/m²K — 15x worse than required.' },
                  { icon: ThermometerSun, title: 'Single-glazed windows (6.35 W/m²K)', desc: 'Original windows in poor condition with fabric failure. Nearly 4x worse than the 1.60 W/m²K Part L limit.' },
                ].map((item, i) => (
                  <Card key={i} className="border-none bg-white shadow-sm">
                    <CardContent className="p-4 flex gap-4">
                      <div className="bg-red-100 text-red-600 p-2 rounded-lg h-fit"><item.icon className="w-5 h-5" /></div>
                      <div><p className="font-medium text-gray-900">{item.title}</p><p className="text-sm text-gray-600">{item.desc}</p></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Heating &amp; Systems</h3>
                {[
                  { icon: Factory, title: 'Gas warm air blower (workshop)', desc: 'High-emission gas-fired warm air heating in the rear workshop. A major drag on the EPC score.' },
                  { icon: ThermometerSun, title: 'Mixed electric fan heaters', desc: 'Electric resistance heating in office areas — expensive to run and penalised in SBEM calculations.' },
                  { icon: TrendingDown, title: 'Gas boiler (first floor)', desc: 'Natural gas boiler serving the first floor offices. Gas heating carries a significant carbon factor penalty.' },
                ].map((item, i) => (
                  <Card key={i} className="border-none bg-white shadow-sm">
                    <CardContent className="p-4 flex gap-4">
                      <div className="bg-amber-100 text-amber-600 p-2 rounded-lg h-fit"><item.icon className="w-5 h-5" /></div>
                      <div><p className="font-medium text-gray-900">{item.title}</p><p className="text-sm text-gray-600">{item.desc}</p></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Per-Unit Improvement Pathways */}
      {units.map((unit, unitIndex) => (
        <section key={unitIndex} className={`py-16 ${unitIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`unit-${unitIndex + 1}-section`}>
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{unit.name}</h2>
                  <p className="text-gray-600 mt-1">{unit.use} &middot; {unit.area}</p>
                </div>
                <div className={`${bandBgColors[unit.baseline.band]} text-white px-4 py-2 rounded-lg font-bold text-xl`}>
                  {unit.baseline.band}{unit.baseline.score}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <Card className="border-none bg-gray-100">
                  <CardContent className="p-3 text-center">
                    <p className="text-xl font-bold text-gray-900">{unit.baseline.emissions}</p>
                    <p className="text-xs text-gray-500">kgCO2/m²/yr</p>
                  </CardContent>
                </Card>
                <Card className="border-none bg-gray-100">
                  <CardContent className="p-3 text-center">
                    <p className="text-xl font-bold text-gray-900">{unit.baseline.energy}</p>
                    <p className="text-xs text-gray-500">kWhpe/m²/yr</p>
                  </CardContent>
                </Card>
                <Card className="border-none bg-gray-100">
                  <CardContent className="p-3 text-center">
                    <p className="text-xl font-bold text-gray-900">{unit.area}</p>
                    <p className="text-xs text-gray-500">Floor Area</p>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-4">
                {unit.stages.map((stage, si) => (
                  <Card key={si} className={`border-2 ${bandColors[stage.band]}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`${bandBgColors[stage.band]} text-white px-3 py-1 rounded-lg font-bold text-lg`}>
                          {stage.band}{stage.score}
                        </div>
                        <h3 className="font-semibold text-gray-900">{stage.label}</h3>
                      </div>
                      <ul className="space-y-2 mb-4">
                        {stage.measures.map((m, mi) => (
                          <li key={mi} className="flex items-start gap-2">
                            <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${bandTextColors[stage.band]}`} />
                            <span className="text-sm text-gray-700">{m}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-gray-600 italic">{stage.note}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Whole Building — Solar PV */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-amber-500 text-white p-2 rounded-lg"><Sun className="w-6 h-6" /></div>
              <h2 className="text-3xl font-bold text-gray-900">Whole Building: The Band A Pathway</h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                With the recommended Stage 3 improvements in place across all three units, the building is positioned for a final push to Band A through whole-building renewable energy generation.
              </p>
              <p>
                We recommended a <strong>solar PV array of up to 24 kWp</strong> on the flat roof, estimated to generate 20,000 to 22,000 kWh per year. Combined with battery storage to maximise self-consumption, this measure is expected to push all three units into Band A — far exceeding any current or proposed MEES requirement.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <Card className="border-2 border-amber-300 bg-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">24 kWp</p>
                  <p className="text-sm text-gray-600 mt-1">Solar PV Array</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-amber-300 bg-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">22,000</p>
                  <p className="text-sm text-gray-600 mt-1">kWh/yr Generation</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-amber-300 bg-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">Band A</p>
                  <p className="text-sm text-gray-600 mt-1">Projected for All Units</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Takeaways</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: Layers, title: 'Unit Splitting Needs Separate EPCs', text: 'Each new unit requires its own EPC before it can be let. The whole-building EPC cannot be used once the building is divided. We modelled all three separately.' },
                { icon: TrendingDown, title: 'Removing Gas is the Biggest Win', text: 'Decommissioning gas heating (blowers and boilers) and replacing with ASHP systems delivered the largest single improvement across all three units.' },
                { icon: Shield, title: 'Fabric First Still Matters', text: 'Roof insulation and window replacement were essential prerequisites. The first floor achieved Band D from fabric measures alone — a jump of 126 points.' },
                { icon: Banknote, title: 'Staged Investment Works', text: 'The client could achieve minimum MEES compliance quickly and affordably, then invest in deeper upgrades as tenants were secured and income flowed.' },
              ].map((item, i) => (
                <Card key={i} className="border-none bg-gray-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-green-100 text-green-600 p-2 rounded-lg"><item.icon className="w-5 h-5" /></div>
                      <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    </div>
                    <p className="text-sm text-gray-700">{item.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Need MEES Advice for Your Commercial Property?</h2>
            <p className="text-gray-600 mb-8">
              Whether you're splitting a building, upgrading for compliance, or future-proofing for 2030, we provide clear, costed improvement roadmaps backed by full SBEM modelling.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button size="lg" className="text-white px-8 rounded-full" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                  Get a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:01onal392980077">
                <Button size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 px-8 rounded-full">
                  <Phone className="w-4 h-4 mr-2" />
                  01392 980077
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <Link to="/services/commercial_advice" className="text-green-600 hover:underline text-sm">Commercial Advice Reports</Link>
              <Link to="/mees-commercial-buildings" className="text-green-600 hover:underline text-sm">MEES Guide</Link>
              <Link to="/industrial-epc" className="text-green-600 hover:underline text-sm">Industrial EPCs</Link>
              <Link to="/case-study-exeter-office-g-to-a" className="text-green-600 hover:underline text-sm">Exeter Office Case Study</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CaseStudyLondonMEES;
